import * as React from 'react';

import {Label} from 'shells/form/label';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import {COUNTRIES} from './consts';
import {getHasError} from './helpers';
import type {AddressForm, AddressValidation, BaseFormDataValidation, BaseFormInfo} from './types';

import styles from './billing-form.module.css';

type Props = {
    baseForm: BaseFormInfo | null | undefined;
    formValidation: BaseFormDataValidation | null | undefined;
    addressForm: AddressForm | null | undefined;
    addressValidation: AddressValidation | null | undefined;
    onUpdateBaseField: (key: string, value: string) => void;
    onUpdateField: (key: string, value: string) => void;
};

export function AddressFields(props: Props) {
    const {
        baseForm,
        formValidation,
        addressForm,
        addressValidation,
        onUpdateBaseField,
        onUpdateField,
    } = props;

    const [countryValue, setCountryValue] = React.useState('');

    React.useEffect(() => {
        if (countryValue) {
            // @ts-expect-error
            const country = COUNTRIES.find((option) => option.label === countryValue);
            if (country) {
                onUpdateField('country', country.value);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryValue]);

    return (
        <StackLegacy spacing={16}>
            <Body size='large' isBold={true}>
                Contact information
            </Body>
            <div>
                <Label htmlFor='companyName'>Company</Label>
                <TextField
                    // @ts-expect-error
                    name='companyName'
                    value={baseForm ? baseForm.companyName : ''}
                    hasError={getHasError(formValidation, 'companyName')}
                    onChange={(value) => onUpdateBaseField('companyName', value)}
                    selectOnFocus={true}
                />
            </div>
            <div>
                <div className='flex gap-8 full-width'>
                    <div className='flex-grow'>
                        <Label htmlFor='email'>Email</Label>
                        <TextField
                            // @ts-expect-error
                            name='email'
                            value={baseForm ? baseForm.email : ''}
                            hasError={getHasError(formValidation, 'email')}
                            onChange={(value) => onUpdateBaseField('email', value)}
                            selectOnFocus={true}
                        />
                    </div>
                    <div className='flex-grow'>
                        <Label htmlFor='phone'>Phone</Label>
                        <TextField
                            // @ts-expect-error
                            name='phone'
                            value={addressForm ? addressForm.phone : ''}
                            required={true}
                            hasError={getHasError(addressValidation, 'phone')}
                            onChange={(value) => onUpdateField('phone', value)}
                            selectOnFocus={true}
                        />
                    </div>
                </div>
            </div>
            <Body size='large' isBold={true}>
                Billing details
            </Body>
            <StackLegacy spacing={16}>
                <div>
                    <Label htmlFor='address1'>Address 1</Label>
                    <TextField
                        // @ts-expect-error
                        name='address1'
                        value={addressForm ? addressForm.address1 : ''}
                        hasError={getHasError(addressValidation, 'address1')}
                        onChange={(value) => onUpdateField('address1', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='address2'>Address 2</Label>
                    <TextField
                        // @ts-expect-error
                        name='address2'
                        value={addressForm ? addressForm.address2 : ''}
                        onChange={(value) => onUpdateField('address2', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='city'>City</Label>
                    <TextField
                        // @ts-expect-error
                        name='city'
                        value={addressForm ? addressForm.city : ''}
                        hasError={getHasError(addressValidation, 'city')}
                        onChange={(value) => onUpdateField('city', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='state'>State</Label>
                    <TextField
                        // @ts-expect-error
                        name='state'
                        value={addressForm ? addressForm.state : ''}
                        hasError={getHasError(addressValidation, 'state')}
                        onChange={(value) => onUpdateField('state', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='postal_code'>Postal code</Label>
                    <TextField
                        // @ts-expect-error
                        name='postal_code'
                        value={addressForm ? addressForm.postal_code : ''}
                        hasError={getHasError(addressValidation, 'postal_code')}
                        onChange={(value) => onUpdateField('postal_code', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='country'>Country</Label>
                    {/* This input is hidden so that the browser autocomplete can fill it out
                    and the value can be set in the select picker */}
                    <input
                        type='text'
                        name='country'
                        value={countryValue}
                        autoComplete='country'
                        onChange={(e) => setCountryValue(e.target.value)}
                        className={styles['hidden-field']}
                    />
                    <SelectPickerGeneric
                        name='country'
                        value={
                            addressForm &&
                            // @ts-expect-error
                            COUNTRIES.find((option) => option.value === addressForm.country)
                        }
                        options={COUNTRIES}
                        // @ts-expect-error
                        onChange={(option) => onUpdateField('country', option.value)}
                        hasError={getHasError(addressValidation, 'country')}
                        // purposely empty so it's clear this isn't filled out
                        placeholder=''
                    />
                </div>
            </StackLegacy>
        </StackLegacy>
    );
}
